import { useEffect, useRef, useState } from "react";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api } from "../lib/api";

type Traffic = Awaited<ReturnType<typeof api.traffic>>;
type HourMix = Awaited<ReturnType<typeof api.hourMix>>;

//: A cohort's reply rate decides whether an hour is worth sending. Measured
//: over the 21 days to 2026-11-03, Waterfall Day 0 replies at 28.4% and Day
//: 30+ at 0.10% - so these thresholds are about the RATE, never the volume.
const COLD_RATE = 1.0;      // below this an hour is cold whatever its size
const RICH_RATE = 5.0;      // above this it is worth having people ready

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
const hourLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric" });

//: the autopilot's mode, in the words the Autopilot page uses
const MODE: Record<string, string> = {
  room: "room to spare",
  metering: "metering",
  catch_up: "catching up",
  off_shift: "nobody on shift",
};

export function TrafficPage() {
  const [t, setT] = useState<Traffic | null>(null);
  const [mix, setMix] = useState<HourMix | null>(null);
  const [showRates, setShowRates] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    // One at a time. This aggregate has taken anything from one second to
    // thirty on the same data, and a fixed interval fired regardless stacks
    // three copies of the slowest query in the system on top of each other.
    const load = () => {
      if (inFlight.current) return;
      inFlight.current = true;
      api
        .traffic()
        .then(setT)
        .catch((e) => setError(e.message))
        .finally(() => {
          inFlight.current = false;
        });
    };
    load();

    // Its own slower loop. The rates behind it come from 21 days of history
    // and the server caches them for half an hour, so asking every 15 seconds
    // would buy nothing and cost a heavy query.
    const loadMix = () => {
      api.hourMix().then(setMix).catch(() => {});
    };
    loadMix();
    const mixId = setInterval(() => {
      if (!document.hidden) loadMix();
    }, 120000);

    // it is a live picture; a stale one is worse than none when deciding
    // whether to press send
    const id = setInterval(() => {
      // Nobody is reading a background tab, and this one aggregate takes
      // seconds over the whole contact table. Refreshed the moment it is
      // looked at again, so what is on screen is never stale while watched.
      if (!document.hidden) load();
    }, 15000);
    const wake = () => {
      if (!document.hidden) load();
      if (!document.hidden) loadMix();
    };
    document.addEventListener("visibilitychange", wake);
    return () => {
      clearInterval(id);
      clearInterval(mixId);
      document.removeEventListener("visibilitychange", wake);
    };
  }, []);

  // This aggregate takes seconds on a real contact table, and returning
  // nothing until it lands left the whole screen blank with no sign that
  // anything was happening.
  if (!t)
    return (
      <>
        <ErrorBanner error={error} />
        {!error && <div className="empty">Counting what is on the wire…</div>}
      </>
    );

  const d = t.today;
  const hourPeak = Math.max(1, ...d.by_hour.map((h) => h.sent + h.planned));

  return (
    <>
      <ErrorBanner error={error} />
      <div className="row w" style={{ marginBottom: 4 }}>
        <h2 style={{ fontSize: 19 }}>Traffic</h2>
        {t.kill_switch && <Pill tone="ba">KILL SWITCH ON — nothing is sending</Pill>}
      </div>
      <p className="dim" style={{ marginTop: -4, fontSize: 13 }}>
        Live, refreshed every 15 seconds.
      </p>

      <div className="tiles">
        <div className="tile">
          <span className="lab">Texts today</span>
          <span className="v">{fmt.n(d.texts_total)}</span>
          <span className="d">
            {fmt.n(d.texts_sent)} sent · {fmt.n(d.texts_to_go)} to go
          </span>
        </div>
        <div className="tile ok">
          <span className="lab">People getting a text today</span>
          <span className="v">{fmt.n(d.people)}</span>
          <span className="d">
            {fmt.n(d.people_sent)} texted so far · of {fmt.n(t.contacts)} on file
          </span>
        </div>
        <div className="tile">
          <span className="lab">Done by</span>
          <span className="v">{!d.sending_day ? "—" : d.finishes_at ? clock(d.finishes_at) : "—"}</span>
          <span className="d">
            {!d.sending_day
              ? "no sending today"
              : d.fits_today
                ? `at ${d.per_minute} a minute`
                : `${fmt.n(d.left_at_close)} wait for the next sending day`}
          </span>
        </div>
        <div className="tile">
          <span className="lab">No text today</span>
          <span className="v">{fmt.n(t.not_today.total)}</span>
          <span className="d">why, below</span>
        </div>
      </div>

      {t.autopilot?.on && (
        <div className="banner stack" style={{ marginBottom: 12 }}>
          {t.autopilot.live ? (
            <>
              <b>
                Autopilot: {MODE[t.autopilot.mode ?? "room"] ?? t.autopilot.mode} — releasing about{" "}
                {t.autopilot.texts_per_hour != null
                  ? `${fmt.n(t.autopilot.texts_per_hour)} work-creating texts an hour`
                  : `${fmt.n(Math.round(t.autopilot.conversations_per_hour ?? 0))} conversations' worth an hour`}
              </b>
              <span className="dim" style={{ fontSize: 12.5 }}>
                {fmt.n(Math.round(t.autopilot.conversations_per_hour ?? 0))} conversations an hour for{" "}
                {t.autopilot.agents ?? 0} on the phones
                {t.autopilot.waiting != null && <> · {Math.round(t.autopilot.waiting)} waiting now</>}
                . The hours below are what is <em>scheduled</em>; the autopilot spreads it to the
                team. Texts that create almost no work are not held and still go at the sending speed.
              </span>
            </>
          ) : (
            <b>Autopilot is on but has not decided a minute yet.</b>
          )}
        </div>
      )}

      {mix && mix.hours.length > 0 && (
        <Card
          title="What each hour is made of"
          actions={
            <button className="btn s" onClick={() => setShowRates((v) => !v)}>
              {showRates ? "hide reply rates" : "reply rates"}
            </button>
          }
          footer={
            <span className="dim">
              Expected replies use this system's own measured rate for each cohort over the last 21
              days — did that person answer within 48 hours. The 27 route tracks went live today and
              have no history, so theirs is borrowed from the closest audience we have measured and
              marked <em>est</em>. Rates vary 280-fold between cohorts, so an hour's size says very
              little on its own.
            </span>
          }
        >
          <div className="row" style={{ gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
            <div>
              <div className="dim" style={{ fontSize: 11 }}>texts today</div>
              <div className="mono" style={{ fontSize: 16 }}>{fmt.n(mix.totals.texts)}</div>
            </div>
            <div>
              <div className="dim" style={{ fontSize: 11 }}>replies to expect</div>
              <div className="mono" style={{ fontSize: 16 }}>~{fmt.n(Math.round(mix.totals.expected_replies))}</div>
            </div>
            <div>
              <div className="dim" style={{ fontSize: 11 }}>never replied to us</div>
              <div className="mono" style={{ fontSize: 16 }}>{mix.totals.never_replied_pct}%</div>
            </div>
            {/* The yardstick. A predicted number with nothing beside it gets
                believed: on 2026-11-03 the model said ~1,403 for a day whose
                recent comparables were 540-785. */}
            {mix.recent_actual_repliers.length > 0 && (
              <div>
                <div className="dim" style={{ fontSize: 11 }}>actually replied, recent days</div>
                <div className="mono" style={{ fontSize: 16 }}>
                  {fmt.n(Math.min(...mix.recent_actual_repliers.map((r) => r.repliers)))}–
                  {fmt.n(Math.max(...mix.recent_actual_repliers.map((r) => r.repliers)))}
                </div>
              </div>
            )}
          </div>

          {mix.totals.expected_replies >
            Math.max(...mix.recent_actual_repliers.map((r) => r.repliers), 0) * 1.25 && (
            <div style={{ fontSize: 11, color: "var(--warn)", marginBottom: 10 }}>
              The expectation is above anything recently achieved, so read it as a ceiling rather
              than a forecast. Almost all of it is borrowed from No CC / CC No Sale, whose people
              had just replied, while today's route cohort was routed on a reply that may be weeks
              old. It corrects itself once these tracks have their own history.
            </div>
          )}

          {showRates && (
            <div style={{ marginBottom: 12, fontSize: 11 }}>
              <div className="dim" style={{ marginBottom: 4 }}>
                measured reply rate per message, last 21 days, 48-hour window
              </div>
              {mix.rates.map((r) => (
                <div className="row" key={`${r.family}-${r.bucket}`} style={{ gap: 8 }}>
                  <span className="mono" style={{ width: 150 }}>{r.family} {r.bucket}</span>
                  <span className="mono" style={{ width: 60, textAlign: "right" }}>{r.rate_pct}%</span>
                  <span className="dim">from {fmt.n(r.sends)} sends</span>
                </div>
              ))}
            </div>
          )}

          {mix.hours.map((h) => {
            const cold = h.expected_rate_pct < COLD_RATE;
            const rich = h.expected_rate_pct >= RICH_RATE;
            return (
              <div
                key={h.hour}
                style={{
                  padding: "7px 0",
                  borderTop: "1px solid var(--line)",
                }}
              >
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <span className="mono" style={{ width: 52, fontSize: 11.5 }}>{hourLabel(h.hour)}</span>
                  <span className="mono" style={{ width: 62, fontSize: 11.5, textAlign: "right" }}>
                    {fmt.n(h.total)}
                  </span>
                  <span className="dim" style={{ fontSize: 11, width: 36 }}>texts</span>
                  <Pill tone={cold ? "ba" : rich ? "ok" : "nu"}>
                    ~{fmt.n(Math.round(h.expected_replies))} replies · {h.expected_rate_pct}%
                  </Pill>
                  {/* How much of that number is borrowed rather than measured.
                      Graded, not a yes/no: today the three big reply hours are
                      76-96% borrowed while the three cold hours are 8-13%, so
                      the reassuring numbers are the guesses and the alarming
                      ones are solid. A single "est" badge hid exactly that. */}
                  {h.estimated_share_pct >= 25 && (
                    <span
                      className="mono"
                      title="Share of this hour's expectation that comes from a borrowed rate, because the cohort has no history of its own yet"
                      style={{ fontSize: 10.5, color: h.estimated_share_pct >= 70 ? "var(--warn)" : "var(--ink3)" }}
                    >
                      {h.estimated_share_pct >= 70 ? "mostly a guess" : "part guess"} ({h.estimated_share_pct}% est)
                    </span>
                  )}
                  <span className="dim" style={{ fontSize: 11 }}>
                    {h.never_replied_pct}% never replied
                  </span>
                  {h.sent > 0 && (
                    <span className="dim" style={{ fontSize: 11 }}>· {fmt.n(h.sent)} already sent</span>
                  )}
                </div>

                <div className="row" style={{ gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                  {h.mix.map((m) => (
                    <span
                      key={m.label}
                      className="mono"
                      style={{ fontSize: 10.5, color: "var(--ink2)", background: "var(--surf2)", border: "1px solid var(--line)", borderRadius: 3, padding: "1px 5px" }}
                    >
                      {m.label} {m.pct}%
                    </span>
                  ))}
                </div>

                {h.flags.map((f) => (
                  <div key={f} style={{ fontSize: 11, marginTop: 4, color: "var(--warn)" }}>
                    {f}
                  </div>
                ))}
              </div>
            );
          })}
        </Card>
      )}

      <Card
        title="Texts per hour today"
        footer={
          <span className="dim">
            Sent is what went; planned is what is due, at the current sending speed. A campaign's
            follow-ups later the same day are included. Change the speed in Settings.
          </span>
        }
      >
        {d.by_hour.length === 0 ? (
          <div className="empty">Nothing is due to go today.</div>
        ) : (
          d.by_hour.map((h) => {
            const total = h.sent + h.planned;
            return (
              <div className="row" key={h.hour} style={{ gap: 8, marginBottom: 4 }}>
                <span className="mono dim" style={{ width: 52, fontSize: 11 }}>
                  {hourLabel(h.hour)}
                </span>
                <div style={{ flex: 1, display: "flex", height: 14, minWidth: 0 }}>
                  <div style={{ width: `${(h.sent / hourPeak) * 100}%`, background: "var(--acc)", borderRadius: "3px 0 0 3px" }} />
                  <div style={{ width: `${(h.planned / hourPeak) * 100}%`, background: "var(--acc-soft)", borderRadius: "0 3px 3px 0" }} />
                </div>
                <span className="mono" style={{ width: 70, fontSize: 11.5, textAlign: "right" }}>{fmt.n(total)}</span>
                <span className="dim" style={{ width: 170, fontSize: 11 }}>
                  {h.sent > 0 && h.planned > 0
                    ? `${fmt.n(h.sent)} sent · ${fmt.n(h.planned)} planned`
                    : h.sent > 0
                      ? "sent"
                      : "planned"}
                </span>
              </div>
            );
          })
        )}
      </Card>

      <Card title="By campaign">
        {d.by_campaign.length === 0 ? (
          <div className="empty">Nothing today.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>What</th>
                  <th style={{ textAlign: "right" }}>Sent</th>
                  <th style={{ textAlign: "right" }}>To go</th>
                  <th style={{ textAlign: "right" }}>Today</th>
                </tr>
              </thead>
              <tbody>
                {d.by_campaign.map((c) => (
                  <tr key={c.name}>
                    <td>{c.name}</td>
                    <td className="mono" style={{ textAlign: "right" }}>{fmt.n(c.sent)}</td>
                    <td className="mono" style={{ textAlign: "right" }}>{fmt.n(c.to_go)}</td>
                    <td className="mono" style={{ textAlign: "right" }}>{fmt.n(c.sent + c.to_go)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Who gets no text today, and why">
        <div className="dim" style={{ fontSize: 12.5, marginBottom: 10 }}>
          {fmt.n(t.not_today.total)} of {fmt.n(t.contacts)} on file. Each person is counted once, under
          the first reason that applies.
        </div>
        {t.not_today.reasons.map((r) => (
          <div className="brk" key={r.key}>
            <span className="dim">{r.label}</span>
            <span className="mono">{fmt.n(r.count)}</span>
          </div>
        ))}
      </Card>

      <Card title="What is queued right now">
        {t.queued_total === 0 ? (
          <div className="empty">Nothing waiting to send.</div>
        ) : (
          <>
            <div className="row w" style={{ gap: 16, marginBottom: 12 }}>
              <div>
                <span className="lab">Queued today</span>
                <div className="num" style={{ fontSize: 24, fontWeight: 650 }}>
                  {fmt.n(t.queued_today)}
                </div>
              </div>
              <div>
                <span className="lab">Queued in total</span>
                <div className="num" style={{ fontSize: 24, fontWeight: 650 }}>
                  {fmt.n(t.queued_total)}
                </div>
              </div>
              <div>
                <span className="lab">Of today's ceiling</span>
                <div
                  className="num"
                  style={{
                    fontSize: 24,
                    fontWeight: 650,
                    color: t.ceiling_used > 80 ? "var(--warn)" : undefined,
                  }}
                >
                  {t.ceiling_used}%
                </div>
              </div>
            </div>

            {t.by_origin.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <span className="lab">Coming from</span>
                {t.by_origin.map((o) => (
                  <div className="brk" key={o.origin}>
                    <span className="dim">{o.origin}</span>
                    <span className="mono">{fmt.n(o.count)}</span>
                  </div>
                ))}
              </div>
            )}

          </>
        )}
      </Card>

      <Card title="What is running">
        <div className="tiles">
          <div className="tile">
            <span className="lab">Active campaigns</span>
            <span className="v">{fmt.n(t.active_campaigns)}</span>
          </div>
          <div className="tile">
            <span className="lab">Paused</span>
            <span className="v">{fmt.n(t.paused_campaigns)}</span>
          </div>
          <div className="tile">
            <span className="lab">Blasts sending</span>
            <span className="v">{fmt.n(t.running_blasts)}</span>
          </div>
          <div className="tile ba">
            <span className="lab">On the block list</span>
            <span className="v">{fmt.n(t.suppressed)}</span>
          </div>
        </div>
      </Card>
    </>
  );
}
